import type { Dispatch, FC, SetStateAction } from 'react';
import { VStack } from '@chakra-ui/react';

import { TeamMembersRequestStatus } from '__generated__/GQL';
import flyingBill from 'assets/brand/flying-bills.png';
import SunshineCard from 'components/_core/SunshineCard';
import Loader from 'components/Loader';

import { BankTransferRequestListPageInfo } from '../../../../hooks/useManagedUserBankTransferRequests';
import { BankTransferRequestsListItemRequestFragment } from '../../../../hooks/useManagedUserBankTransferRequests/managedUserBankTransferRequests.gql';
import StatusBar from '../BankTransferRequestsList/components/StatusBar';
import EmptyView from '../EmptyView';
import BankTransferRequestListItem from './components/BankTransferRequestListItem';
import locales from './locales';

interface BankTransferRequestsListProps {
  selectedBankTransferRequestId?: string;
  requests: BankTransferRequestsListItemRequestFragment[];
  status: TeamMembersRequestStatus[];
  onStatusChange: Dispatch<SetStateAction<TeamMembersRequestStatus[]>>;
  isLoading: boolean;
  isLoadingMore: boolean;
  pageInfo: BankTransferRequestListPageInfo;
}

const getTitle = (status: TeamMembersRequestStatus[]) => {
  if (status.includes(TeamMembersRequestStatus.Pending)) {
    return locales.emptyTitle.pending;
  }

  if (
    status.includes(TeamMembersRequestStatus.Approved) ||
    status.includes(TeamMembersRequestStatus.Processed)
  ) {
    return locales.emptyTitle.processing;
  }

  if (status.includes(TeamMembersRequestStatus.Completed)) {
    return locales.emptyTitle.done;
  }

  if (
    status.includes(TeamMembersRequestStatus.Canceled) ||
    status.includes(TeamMembersRequestStatus.Rejected)
  ) {
    return locales.emptyTitle.canceled;
  }

  return locales.emptyTitle.pending;
};

const BankTransferRequestsList: FC<BankTransferRequestsListProps> = ({
  isLoading,
  isLoadingMore,
  onStatusChange,
  pageInfo,
  requests,
  selectedBankTransferRequestId,
  status,
}) => {
  const hasRequests = requests.length > 0;

  return (
    <VStack alignItems="flex-start" spacing="space-24">
      <StatusBar onStatusChange={onStatusChange} status={status} />
      {!isLoadingMore && isLoading ? (
        <Loader />
      ) : (
        <>
          {hasRequests ? (
            <SunshineCard.Group>
              {requests.map((request) => (
                <BankTransferRequestListItem
                  isSelected={
                    selectedBankTransferRequestId ===
                    request.teamMembersRequestId
                  }
                  key={request.teamMembersRequestId}
                  request={request}
                />
              ))}
            </SunshineCard.Group>
          ) : (
            <>
              {!pageInfo.hasNextPage ? (
                <EmptyView
                  imageSrc={flyingBill}
                  marginX="auto"
                  maxWidth={372}
                  title={getTitle(status)}
                />
              ) : null}
            </>
          )}
        </>
      )}
    </VStack>
  );
};

export default BankTransferRequestsList;
